/**
 * CardManagement — per-card Stripe payment method management for Profile page.
 * Lists individual cards with brand/last4/expiry and remove button.
 */

import React, { useState, useEffect, useCallback } from 'react';
import '../styles/CardManagement.css';

export default function CardManagement() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [warning, setWarning] = useState('');

  const fetchMethods = useCallback(async () => {
    try {
      const res = await fetch('/api/customer/payment-methods', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMethods(data.payment_methods || []);
      }
    } catch (err) {
      console.warn('Failed to fetch payment methods:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMethods(); }, [fetchMethods]);

  const handleRemove = async (pmId) => {
    const method = methods.find(m => m.id === pmId);
    const label = method?.brand
      ? `${method.brand} ending in ${method.last4}`
      : 'this payment method';

    if (!window.confirm(`Remove ${label}?`)) return;

    setRemovingId(pmId);
    setWarning('');
    try {
      const res = await fetch(`/api/customer/payment-methods/${pmId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || 'Failed to remove card');
        return;
      }
      if (data.hosting_warning) {
        setWarning(data.hosting_warning);
      }
      await fetchMethods();
    } catch (err) {
      console.error('Remove card error:', err);
    } finally {
      setRemovingId(null);
    }
  };

  const formatBrand = (brand) => {
    if (!brand) return 'Card';
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  if (loading) {
    return (
      <div className="card-management">
        <h4>Card</h4>
        <p className="card-hint">Loading...</p>
      </div>
    );
  }

  return (
    <div className="card-management">
      <h4>Card</h4>

      {warning && (
        <div className="card-warning">{warning}</div>
      )}

      {methods.length === 0 ? (
        <div className="card-not-linked">
          <p className="card-hint">
            No cards on file. You'll add one when you approve your first project.
          </p>
        </div>
      ) : (
        <div className="card-list">
          {methods.map(m => (
            <div key={m.id} className="card-list-item">
              <div className="card-info">
		  <span className="card-brand">{formatBrand(m.brand)}</span>
		  <span className="card-last4">
		    {m.brand === 'link' ? m.last4 : `•••• ${m.last4} — one click payment`}
		  </span>
		  {m.exp_month && m.exp_year && (
                  <span className="card-exp">
                    {String(m.exp_month).padStart(2, '0')}/{String(m.exp_year).slice(-2)}
                  </span>
                )}
              </div>
              <button
                className="card-remove-btn"
                onClick={() => handleRemove(m.id)}
                disabled={removingId === m.id}
              >
                {removingId === m.id ? '...' : 'Remove'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
